import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import StudyForm from "./StudyForm";
import ViewerFormModal from "./ViewerFormModal";
import ViewerPreviewModal from "./ViewerPreviewModal";
import "./Studies.css";
import "../datasets/Datasets.css";
import "../shared/starfield.css";

const StudyList = ({ mode = "studies" }) => {
    // mode: "studies" | "viewers" | "both"
    const showStudies = mode === "studies" || mode === "both";
    const showViewers = mode === "viewers" || mode === "both";
    const navigate = useNavigate();
    const [studies, setStudies] = useState([]);
    const [viewers, setViewers] = useState([]);
    const [aiModels, setAiModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateStudyModal, setShowCreateStudyModal] = useState(false);
    const [showCreateViewerModal, setShowCreateViewerModal] = useState(false);
    const [editingViewer, setEditingViewer] = useState(null);
    const [previewingViewer, setPreviewingViewer] = useState(null);
    const [activeStudyTab, setActiveStudyTab] = useState("all");
    const [activeMainTab, setActiveMainTab] = useState(showStudies ? "studies" : "viewers");

    // React-Router keeps this component mounted when navigating between
    // /Studies and /Viewers (same component, different `mode` prop). Without
    // this sync, the internal tab stays stuck on whatever mode it was
    // initially mounted with.
    useEffect(() => {
        setActiveMainTab(mode === "viewers" ? "viewers" : "studies");
    }, [mode]);

    const currentUsername = localStorage.getItem("username");
    const importFileInputRef = useRef(null);

    const fetchStudies = useCallback(async () => {
        try {
            const response = await API.get("/studies/");
            setStudies(response.data);
        } catch (err) {
            console.error("Error fetching studies:", err);
            toast.error("Failed to fetch studies.");
        }
    }, []);

    const fetchViewers = useCallback(async () => {
        try {
            const response = await API.get("/viewers/");
            setViewers(response.data);
        } catch (err) {
            console.error("Error fetching viewers:", err);
            toast.error("Failed to fetch viewers.");
        }
    }, []);

    // Needed to compute Models / Tools counts per study — each DVAIModel exposes
    // a `studies` PK list, so we bucket them per study client-side.
    const fetchAiModels = useCallback(async () => {
        try {
            const response = await API.get("/models/");
            setAiModels(Array.isArray(response.data) ? response.data : (response.data?.results ?? []));
        } catch (err) {
            console.error("Error fetching AI models:", err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchStudies(), fetchViewers(), fetchAiModels()]);
            setLoading(false);
        };
        loadData();
    }, [fetchStudies, fetchViewers, fetchAiModels]);

    const handleStudyClick = (studyId) => {
        navigate(`/Study/${studyId}`);
    };

    const handleDeleteViewer = async (viewerId, viewerName) => {
        if (!window.confirm(`Are you sure you want to delete "${viewerName}"?`)) {
            return;
        }
        try {
            await API.delete(`/viewers/${viewerId}/`);
            toast.success("Viewer deleted successfully.");
            fetchViewers();
        } catch (err) {
            console.error("Error deleting viewer:", err);
            if (err.response?.data?.error) {
                toast.error(err.response.data.error);
            } else {
                toast.error("Failed to delete viewer.");
            }
        }
    };

    const handleDownloadTemplate = (viewer) => {
        const template = {
            template_version: 1,
            exported_at: new Date().toISOString(),
            viewer: {
                name: viewer.name,
                description: viewer.description || "",
                viewer_type: viewer.viewer_type,
                supported_data_types: viewer.supported_data_types,
                custom_url: viewer.custom_url || null,
                icon: viewer.icon,
                color: viewer.color,
                is_active: viewer.is_active,
                config: viewer.config || {},
            },
        };
        const blob = new Blob([JSON.stringify(template, null, 2)], {
            type: "application/json",
        });
        const slug = (viewer.name || "viewer")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "viewer";
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}.viewer-template.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleImportClick = () => {
        importFileInputRef.current?.click();
    };

    const handleImportTemplate = async (event) => {
        const file = event.target.files?.[0];
        // Reset the input so picking the same file twice still fires onChange.
        event.target.value = "";
        if (!file) return;

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const v = parsed?.viewer;
            if (!v || typeof v !== "object" || !v.name) {
                toast.error("Invalid template file: missing viewer data.");
                return;
            }

            const existingNames = new Set(viewers.map((x) => x.name));
            let name = v.name;
            if (existingNames.has(name)) {
                let i = 2;
                while (existingNames.has(`${v.name} (${i})`)) i++;
                name = `${v.name} (${i})`;
            }

            const payload = {
                name,
                description: v.description || "",
                viewer_type: v.viewer_type || "custom",
                supported_data_types: v.supported_data_types || "multimodal",
                custom_url: v.custom_url || null,
                icon: v.icon || "fa-desktop",
                color: v.color || "#007bff",
                is_active: v.is_active !== false,
                config: v.config || {},
            };

            await API.post("/viewers/", payload);
            toast.success(`Viewer "${name}" imported successfully.`);
            fetchViewers();
        } catch (err) {
            console.error("Error importing viewer template:", err);
            if (err instanceof SyntaxError) {
                toast.error("Failed to parse template: invalid JSON.");
            } else {
                toast.error("Failed to import viewer template.");
            }
        }
    };

    const filteredStudies = studies.filter((study) => {
        if (activeStudyTab === "owned") {
            return study.created_by_username === currentUsername;
        }
        if (activeStudyTab === "assigned") {
            return study.user_assignment !== null;
        }
        return true;
    });

    const getStatusBadge = (status) => {
        const colors = {
            draft: "secondary",
            active: "primary",
            completed: "success",
            archived: "dark",
        };
        return colors[status] || "secondary";
    };

    const getViewerTypeIcon = (viewerType) => {
        const iconMap = {
            'ohif': 'fa-eye',
            'ohif-segmentation': 'fa-cut',
            'ohif-3d': 'fa-cube',
            'ohif-microscopy': 'fa-microscope',
            'kitware-volview': 'fa-cube',
            'stone-web-viewer': 'fa-image',
            'dv2': 'fa-desktop',
            'discovery-viewer-test': 'fa-flask',
            'image-viewer': 'fa-image',
            'ehr-viewer': 'fa-heartbeat',
            'signal-viewer': 'fa-wave-square',
            'pme': 'fa-microscope',
            'custom': 'fa-cog',
        };
        return iconMap[viewerType] || 'fa-desktop';
    };

    // Calculate stats
    const totalStudies = studies.length;
    const activeStudiesCount = studies.filter(s => s.status === 'active').length;
    const totalViewers = viewers.length;

    // Per-study derived counts. `studies` on a model is a PK list; entity_type
    // discriminates models vs tools. Assignments carry a `role` (reviewer/lead/contributor).
    const countModelsForStudy = (studyId) =>
        aiModels.filter(m => (m.entity_type !== "tool") && (m.studies || []).includes(studyId)).length;
    const countToolsForStudy = (studyId) =>
        aiModels.filter(m => m.entity_type === "tool" && (m.studies || []).includes(studyId)).length;
    const countReviewersForStudy = (study) =>
        (study.assignments || []).filter(a => a.role === "reviewer").length;

    return (
        <div className="studies-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            <Navbar />

            <div className="container py-4">
                {/* Back Button */}
                <button className="back-button" onClick={() => navigate("/Dashboard")}>
                    <i className="fa fa-arrow-left me-2"></i>
                    Back
                </button>

                {/* Header Card */}
                <div className="datasets-header-card">
                    <div className="datasets-header-content">
                        <div className="datasets-info">
                            <h1 className="datasets-title">
                                {mode === "viewers" ? "Viewers" : mode === "studies" ? "Studies" : "Studies & Viewers"}
                            </h1>
                            <p className="datasets-description">
                                {mode === "viewers"
                                    ? "Configure viewers for data visualization"
                                    : mode === "studies"
                                        ? "Manage your research studies"
                                        : "Manage your research studies and configure viewers for data visualization"}
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            {showStudies && (
                                <button
                                    className="btn-create-dataset"
                                    onClick={() => setShowCreateStudyModal(true)}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    New Study
                                </button>
                            )}
                            {showViewers && (
                                <>
                                    <button
                                        className="btn-create-dataset"
                                        style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' }}
                                        onClick={() => navigate("/ViewerCreationLab")}
                                    >
                                        <i className="fa fa-plus me-2"></i>
                                        New Viewer
                                    </button>
                                    <button
                                        className="btn-create-dataset"
                                        style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #4b2a85 100%)' }}
                                        onClick={handleImportClick}
                                        title="Upload a viewer template JSON to create a new viewer"
                                    >
                                        <i className="fa fa-upload me-2"></i>
                                        Import Template
                                    </button>
                                    <input
                                        ref={importFileInputRef}
                                        type="file"
                                        accept="application/json,.json"
                                        style={{ display: "none" }}
                                        onChange={handleImportTemplate}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="datasets-stats">
                    {showStudies && (
                        <>
                            <div className="stat-card">
                                <i className="fa fa-flask stat-icon"></i>
                                <div className="stat-value">{totalStudies}</div>
                                <div className="stat-label">Total Studies</div>
                            </div>
                            <div className="stat-card">
                                <i className="fa fa-play-circle stat-icon"></i>
                                <div className="stat-value">{activeStudiesCount}</div>
                                <div className="stat-label">Active Studies</div>
                            </div>
                        </>
                    )}
                    {showViewers && (
                        <div className="stat-card">
                            <i className="fa fa-desktop stat-icon"></i>
                            <div className="stat-value">{totalViewers}</div>
                            <div className="stat-label">Total Viewers</div>
                        </div>
                    )}
                </div>

                {/* Main Tab Navigation (only when both are shown) */}
                {mode === "both" && (
                    <div className="filter-tabs mb-4">
                        <button
                            className={`filter-tab ${activeMainTab === "studies" ? "active" : ""}`}
                            onClick={() => setActiveMainTab("studies")}
                        >
                            <i className="fa fa-flask me-2"></i>
                            Studies ({studies.length})
                        </button>
                        <button
                            className={`filter-tab ${activeMainTab === "viewers" ? "active" : ""}`}
                            onClick={() => setActiveMainTab("viewers")}
                        >
                            <i className="fa fa-desktop me-2"></i>
                            Viewers ({viewers.length})
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : activeMainTab === "studies" ? (
                    /* Studies Section */
                    <div className="datasets-card">
                        <div className="datasets-card-header">
                            <div className="datasets-card-title">
                                <i className="fa fa-flask datasets-card-icon"></i>
                                <div>
                                    <h3>All Studies</h3>
                                    <p className="datasets-card-subtitle">
                                        Click on a study to view details
                                    </p>
                                </div>
                            </div>
                            {/* Study Filter Tabs */}
                            <div className="d-flex gap-2">
                                <button
                                    className={`btn btn-sm ${activeStudyTab === "all" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setActiveStudyTab("all")}
                                >
                                    All
                                </button>
                                <button
                                    className={`btn btn-sm ${activeStudyTab === "owned" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setActiveStudyTab("owned")}
                                >
                                    Owned Studies
                                </button>
                                <button
                                    className={`btn btn-sm ${activeStudyTab === "assigned" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setActiveStudyTab("assigned")}
                                >
                                    Assigned to Me
                                </button>
                            </div>
                        </div>
                        <div className="datasets-card-content">
                            {filteredStudies.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fa fa-flask"></i>
                                    <p>No studies found</p>
                                </div>
                            ) : (
                                <table className="datasets-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Public</th>
                                            <th>Project</th>
                                            <th>Datasets</th>
                                            <th>Models</th>
                                            <th>Tools</th>
                                            <th>Reviewers</th>
                                            <th>Created By</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStudies.map((study) => (
                                            <tr
                                                key={study.id}
                                                className="dataset-row"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleStudyClick(study.id)}
                                            >
                                                <td>
                                                    <strong>{study.name}</strong>
                                                </td>
                                                <td className="description-cell">
                                                    {study.description || "—"}
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getStatusBadge(study.status)}`}>
                                                        {study.status || "draft"}
                                                    </span>
                                                    {study.user_assignment && (
                                                        <span className={`badge bg-${study.user_assignment.role === 'lead' ? 'warning' : 'info'} ms-1`}>
                                                            {study.user_assignment.role}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {study.is_public ? (
                                                        <span className="badge bg-success">
                                                            <i className="fa fa-globe me-1"></i>
                                                            Public
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-secondary">
                                                            <i className="fa fa-lock me-1"></i>
                                                            Private
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {study.project_name ? (
                                                        <span className="project-badge">{study.project_name}</span>
                                                    ) : (
                                                        <span className="no-project">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="image-count-badge">
                                                        {study.dataset_count || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="image-count-badge">
                                                        {countModelsForStudy(study.id)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="image-count-badge">
                                                        {countToolsForStudy(study.id)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="image-count-badge">
                                                        {countReviewersForStudy(study)}
                                                    </span>
                                                </td>
                                                <td>{study.created_by_username || "—"}</td>
                                                <td>{study.created_at ? new Date(study.created_at).toLocaleDateString() : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Viewers Section */
                    <div className="datasets-card">
                        <div className="datasets-card-header">
                            <div className="datasets-card-title">
                                <i className="fa fa-desktop datasets-card-icon"></i>
                                <div>
                                    <h3>All Viewers</h3>
                                    <p className="datasets-card-subtitle">
                                        Configure and assign viewers to studies
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="datasets-card-content">
                            {viewers.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fa fa-desktop"></i>
                                    <p>No viewers configured yet</p>
                                </div>
                            ) : (
                                <table className="datasets-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Data Viewers</th>
                                            <th>Studies</th>
                                            <th>Created By</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewers.map((viewer) => (
                                            <tr key={viewer.id} className="dataset-row">
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i
                                                            className={`fa ${getViewerTypeIcon(viewer.viewer_type)}`}
                                                            style={{ color: viewer.color || '#007bff' }}
                                                        ></i>
                                                        <strong>{viewer.name}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {viewer.viewer_type_display || viewer.viewer_type}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="image-count-badge">
                                                        {viewer.study_count || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    {viewer.created_by_user?.username || "—"}
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-action btn-view"
                                                            onClick={() => setPreviewingViewer(viewer)}
                                                            title="Preview Viewer Configuration"
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                        <button
                                                            className="btn-action btn-edit"
                                                            onClick={() => navigate(`/ViewerCreationLab?viewer_id=${viewer.id}`)}
                                                            title="Edit Viewer in the Discovery Viewer Creation Lab"
                                                        >
                                                            <i className="fa fa-pencil"></i>
                                                        </button>
                                                        {!viewer.is_system && (
                                                            <button
                                                                className="btn-action btn-delete"
                                                                onClick={() => handleDeleteViewer(viewer.id, viewer.name)}
                                                                title="Delete Viewer"
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* Create Study Modal */}
                {showCreateStudyModal && (
                    <StudyForm
                        onClose={() => setShowCreateStudyModal(false)}
                        onSuccess={() => {
                            setShowCreateStudyModal(false);
                            fetchStudies();
                        }}
                    />
                )}

                {/* Create/Edit Viewer Modal */}
                {showCreateViewerModal && (
                    <ViewerFormModal
                        viewer={editingViewer}
                        onClose={() => {
                            setShowCreateViewerModal(false);
                            setEditingViewer(null);
                        }}
                        onSuccess={() => {
                            setShowCreateViewerModal(false);
                            setEditingViewer(null);
                            fetchViewers();
                        }}
                    />
                )}

                {/* Viewer Preview Modal */}
                {previewingViewer && (
                    <ViewerPreviewModal
                        viewer={previewingViewer}
                        onClose={() => setPreviewingViewer(null)}
                        onDownloadTemplate={handleDownloadTemplate}
                    />
                )}

            </div>
        </div>
    );
};

export default StudyList;
